<template>
  <div class="register-wrapper" ref="registerRef">
    <div class="scroll-wrapper">
      <div class="form-title" v-if="isPhonePwd">支持账号/手机号注册</div>
      <van-form
        class="auth-form"
        ref="formRef"
        required="auto"
        label-align="top"
        validate-trigger="onSubmit"
        @submit="onSubmit"
      >
        <template v-for="col in columns" :key="col.dataIndex">
          <form-input-password
            v-model="formValues[col.dataIndex]"
            :label="col.label"
            :name="col.dataIndex"
            :placeholder="col.placeholder"
            :left-icon="col.icon"
            :required="col.required"
            :message="col.ruleMessage"
            :rules="col.rules"
            v-bind="col.fieldProps"
            is-open
            v-if="col.valueType === 'password'"
          />
          <form-input-verify-code
            v-model="formValues[col.dataIndex]"
            :label="col.label"
            :name="col.dataIndex"
            :placeholder="col.placeholder"
            :left-icon="col.icon"
            :message="col.ruleMessage"
            :required="col.required"
            :login-type="registerType"
            :account="formValues[verifyAccount]"
            :country="checkedCountry?.key"
            :type="2"
            v-else-if="col.valueType === 'verifyCode'"
          />
          <form-input-phone
            v-model="formValues[col.dataIndex]"
            :label="col.label"
            :name="col.dataIndex"
            :placeholder="col.placeholder"
            :left-icon="col.icon"
            :message="col.ruleMessage"
            :required="col.required"
            :rules="col.rules"
            @checked="selectCountry"
            v-else-if="col.valueType === 'phone'"
          />
          <form-input-phone-account
            v-model="formValues[col.dataIndex]"
            :label="col.label"
            :name="col.dataIndex"
            :placeholder="col.placeholder"
            :message="col.ruleMessage"
            :required="col.required"
            :rules="col.rules"
            :isPhone="isPhonePwdPhone"
            @checked="selectCountry"
            v-else-if="col.valueType === 'phoneAccount'"
          />
          <authentication-method-switch v-model="switchType" type="register" v-else-if="col.valueType === 'custom'" />

          <van-field
            class="from-input"
            v-model="formValues[col.dataIndex]"
            :type="col.valueType"
            :name="col.dataIndex"
            :placeholder="col.placeholder"
            :rules="
              col.rules
                ? [{ required: col.required, message: col.ruleMessage }, ...col.rules]
                : [{ required: col.required, message: col.ruleMessage }]
            "
            :formatter="(text: string) => trim(text)"
            v-bind="col.fieldProps"
            v-else
          >
            <template #right-icon v-if="!!col.icon">
              <svg-icon :icon-name="col.icon" :width="col.icon === 'account' ? 46 : 43" current-color />
            </template>
          </van-field>
        </template>
        <van-cell class="tools" v-if="isAccount || isPhonePwd">
          <van-checkbox v-model="isSaveAccount" shape="round">
            {{ isPhonePwdPhone ? '记住手机号' : '记住账号密码' }}
          </van-checkbox>
        </van-cell>
        <!-- <van-cell class="tools">
          <van-checkbox v-model="isRead" shape="round">
            我已阅读并同意
            <span class="light">相关条款</span>
            和
            <span class="light">隐私政策</span>
          </van-checkbox>
        </van-cell> -->

        <div id="aiValidate2" class="ai-validate"></div>

        <van-sticky :container="registerRef" :offset-bottom="actionsBarHeight" position="bottom">
          <van-cell class="submit-fixed1">
            <van-button class="submit-btn" type="primary" native-type="submit" :loading="loading" round block>
              注册
            </van-button>
          </van-cell>
        </van-sticky>
      </van-form>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineEmits, onMounted, onUnmounted, ref } from 'vue';
import type { FieldProps, FieldRule, FormInstance } from 'vant';
import { showFailToast, showNotify } from 'vant';
import FormInputPassword from '@/components/FormInputPassword/index.vue';
import FormInputVerifyCode from '@/components/FormInputVerifyCode/index.vue';
import FormInputPhone from '@/components/FormInputPhone/index.vue';
import FormInputPhoneAccount from '@/components/FormInputPhoneAccount/index.vue';
import { includes, omit, omitBy, pickBy, trim } from 'lodash-es';
import { storeToRefs, useTenantStore, useUserStore } from '@star/stores';
import { mb_t } from '@star/languages';
import { AuthStorageKeysEnum } from '@star/constants';
import { promiseTimeout, StorageSerializers, useLocalStorage } from '@vueuse/core';
import { checkBlankReg, checkEmailReg, checkPhoneReg, checkPwd, getSession, isPhoneNumber, md5 } from '@star/utils';
import { pwdLevelText } from '@/hooks/common';
import { useAiValidate, useNEGuardian } from '@star/hooks';
import { config } from '@star/config';
import { useRoute } from 'vue-router';
import AuthenticationMethodSwitch from '@/layout/header/components/loginRegister/AuthenticationMethodSwitch.vue';

const route = useRoute();

type ColumnData = {
  label?: string;
  valueType?: string;
  dataIndex: string;
  placeholder?: string;
  ruleMessage?: string;
  required?: boolean;
  disabled?: boolean;
  icon?: string;
  show: boolean;
  hidden: boolean;
  fieldProps?: FieldProps;
  rules?: FieldRule[];
};

type Props = {
  visible?: boolean;
  registerType: string;
  actionsBarHeight: number;
};

const props = withDefaults(defineProps<Props>(), {
  visible: false,
});

const emit = defineEmits(['success']);

const { register, reqEmailRigister, reqSMSRigister, reqInvicode, fetchVerifyCode } = useUserStore();
const { sysRegisterOptions, custContact, tenantInfo } = storeToRefs(useTenantStore());

const { getNegProductID, initNeg, getNegToken, token: negToken } = useNEGuardian(); // 网易风控hooks

const registerRef = ref<any>();
const formRef = ref<FormInstance>();
const isRead = ref<boolean>(true);
const loading = ref<boolean>(false);
const checkedCountry = ref<Record<string, any>>();
const isReadonlyInviteCode = ref<boolean>(false);
const isSaveAccount = ref<boolean>(true);
const switchType = ref<'1' | '2'>('1'); // 密码或者验证码登录

const inviteCodeDefault = route.query.code || getSession('INVITE_CODE') || undefined;

const formValues = ref<Record<string, any>>({
  cust_name: undefined,
  password: undefined,
  phone: undefined,
  smsCode: undefined,
  emailCode: undefined,
  contact_name: undefined,
  nickname: undefined,
  zalo: undefined,
  line: undefined,
  facebook: undefined,
  telegram: undefined,
  whatsApp: undefined,
  email: undefined,
  qq: undefined,
  inviteCode: inviteCodeDefault,
  isQp: 1,
  link_addr: '',
  verifycode: undefined, // 图片验证
  validate: undefined, // 三方智能验证code
  // special_problems: undefined, // 特殊验证
  // app_key: '',
  // mobile_phone: undefined,
});

const loginApi = {
  account: register,
  phone_captcha: reqSMSRigister,
  email_captcha: reqEmailRigister,
  // email_pwd_captcha: reqEmailPwdlogin,
};

// 图形码验证
const isImageVerifyCode = computed(() => {
  return sysRegisterOptions.value.CUST_REGISTER_CAPTCHA_MODE.PICTURE_SWITCH === '1';
});

// 易盾智能验证
const isAiVerify = computed(() => {
  return (
    sysRegisterOptions.value?.CUST_REGISTER_CAPTCHA_MODE?.SMART_SWITCH === '1' &&
    sysRegisterOptions.value?.CUST_REGISTER_CAPTCHA_MODE?.Z2_M_SMART_CAPTCHA_MODE !== '0'
  );
});

const loginSaveAccountInfo = useLocalStorage(
  AuthStorageKeysEnum.LOGIN_SAVE_ACCOUNT_INFO,
  {
    isSave: false,
  },
  {
    serializer: StorageSerializers.object,
  }
);

const saveAccountHandle = () => {
  if (isSaveAccount.value) {
    loginSaveAccountInfo.value = {
      ...loginSaveAccountInfo.value,
      ...pickBy(formValues.value, (val, key) =>
        includes(['cust_name', 'emailName', 'receiveMobile', 'phone', 'password'], key)
      ),
      isSave: true,
    };
  } else {
    loginSaveAccountInfo.value = {
      isSave: false,
    };
  }
};
// 去除所有空格
const removeSpaces = value => {
  return value.replace(/\s+/g, '');
};

const isAccount = computed(() => {
  return props.registerType === 'account';
});

const isEmailCode = computed(() => {
  return props.registerType === 'email_captcha';
});

const isEmailPassword = computed(() => {
  return props.registerType === 'email_pwd_captcha';
});

const isPhone = computed(() => {
  return props.registerType === 'phone_captcha';
});

const isPhonePwd = computed(() => {
  return props.registerType === 'phone_pwd_captcha';
});

const isPhonePwdPhone = computed(() => {
  return props.registerType === 'phone_pwd_captcha' && isPhoneNumber(formValues.value.phone);
});

const loginMethodSwitch = computed(() => {
  return tenantInfo.value.sysOptions?.REGISTER_SHOW_METHOD_SWITCH === '1';
});

const verifyAccount = computed(() => {
  if (isPhone.value || isPhonePwdPhone.value) {
    return 'phone';
  } else if (isEmailCode.value) {
    return 'cust_name';
  } else {
    return '';
  }
});

const validateMaps = computed(() => {
  return {
    confirmPassword: {
      show: custContact.value.Z1_confirm_password === 1,
      required: custContact.value.Z2_confirm_password === 1,
    },
    acctPwd: {
      show: custContact.value.Z1_withdrawal_password === 1,
      // custContact.value.Z1_withdrawal_password === 1 ||
      // sysRegisterOptions.value?.CUST_REGISTER_MODE?.transaction_password === '1',
      required: custContact.value.Z2_withdrawal_password === 1,
      // custContact.value.Z2_withdrawal_password === 1 ||
      // sysRegisterOptions.value?.CUST_REGISTER_MODE?.transaction_password === '1',
    },
    pwdLevel: sysRegisterOptions.value?.CUST_REGISTER_PASSWORD_MODE?.PASSWORD_MODE ?? '1',
  };
});

const commonTriggerList = ['onChange', 'onBlur', 'onSubmit'];

const columns = computed(() => {
  return [
    {
      label: '',
      valueType: 'text',
      dataIndex: 'cust_name',
      placeholder: '用户名',
      ruleMessage: '请输入用户名',
      icon: 'account',
      required: true,
      fieldProps: {
        maxlength: 20,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkBlankReg(val)) {
              return '请输入正确的用户名';
            }
            return true;
          },
          trigger: formValues.value.cust_name ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: isAccount.value || isPhone.value,
    },
    {
      label: '',
      valueType: 'phoneAccount',
      dataIndex: 'phone',
      placeholder: '账号/手机号注册',
      ruleMessage: '请输入账号或手机号码',
      icon: 'cellphone',
      required: true,
      fieldProps: {
        // maxlength: 11,
        // minlength: 6,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (isPhonePwdPhone.value) {
              if (!checkPhoneReg(val)) {
                return '请输入正确的手机号';
              }
            } else {
              const pattern = /^[a-zA-Z0-9]{5,12}$/;
              if (!checkBlankReg(val) || !pattern.test(val)) {
                return '账号格式错误，请输入5-12位英文/数字';
              }
            }
            return true;
          },
          trigger: formValues.value.phone ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: isPhonePwd.value,
    },
    {
      label: '',
      valueType: 'custom',
      dataIndex: '',
      show: !formValues.value.phone && loginMethodSwitch.value,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'cust_name',
      placeholder: '邮箱',
      ruleMessage: '请输入邮箱账号',
      icon: 'email',
      required: true,
      fieldProps: {
        maxlength: 50,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkEmailReg(val)) {
              return '请输入正确的邮箱账号';
            }
            return true;
          },
          trigger: formValues.value.cust_name ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: isEmailCode.value,
    },
    {
      label: '',
      valueType: 'verifyCode',
      dataIndex: 'emailCode',
      placeholder: '邮箱验证码',
      ruleMessage: '请输入邮箱验证码',
      icon: 'yzm_code',
      required: true,
      fieldProps: {
        maxlength: 8,
        clearable: true,
      },
      show: isEmailCode.value,
    },
    {
      label: '',
      valueType: 'phone',
      dataIndex: 'phone',
      placeholder: '手机号',
      ruleMessage: '请输入手机号码',
      icon: 'cellphone',
      required: true,
      fieldProps: {
        maxlength: 11,
        minlength: 6,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkPhoneReg(val)) {
              return '请输入正确的手机号';
            }
            return true;
          },
          trigger: formValues.value.phone ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: isPhone.value,
    },

    {
      label: '',
      valueType: 'verifyCode',
      dataIndex: 'smsCode',
      placeholder: '手机验证码',
      ruleMessage: '请输入手机验证码',
      icon: 'yzm_code',
      required: true,
      fieldProps: {
        maxlength: 8,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: isPhone.value || (!!formValues.value.phone ? isPhonePwdPhone.value : switchType.value === '2'),
    },
    // {
    //   label: '',
    //   valueType: 'text',
    //   dataIndex: 'emailName',
    //   placeholder: '邮箱账号',
    //   ruleMessage: '请输入邮箱账号',
    //   icon: 'email',
    //   required: true,
    //   fieldProps: {
    //     maxlength: 50,
    //     clearable: true,
    //     // formatter: (text: string) => trim(text),
    //   },
    //   show: isEmailPassword.value,
    // },
    {
      label: '',
      valueType: 'password',
      dataIndex: 'password',
      placeholder: '密码',
      ruleMessage: '请输入密码',
      icon: 'password',
      required: true,
      fieldProps: {
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkPwd(val, validateMaps.value.pwdLevel)) {
              return pwdLevelText(validateMaps.value.pwdLevel);
            }
            return true;
          },
          trigger: formValues.value.password ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show:
        (!!formValues.value.phone && !isPhonePwdPhone.value) || (!formValues.value.phone && switchType.value === '1'),
    },
    {
      label: '',
      valueType: 'password',
      dataIndex: 'password2',
      placeholder: '确认密码',
      ruleMessage: '请输入确认密码',
      icon: 'password',
      required: custContact.value.Z2_confirm_password === 1,
      show: custContact.value.Z1_confirm_password === 1,
      fieldProps: {
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkPwd(val, validateMaps.value.pwdLevel)) {
              return pwdLevelText(validateMaps.value.pwdLevel);
            }
            return true;
          },
          trigger: formValues.value.password2 ? ['onBlur'] : ['onSubmit'],
        },
      ],
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'contact_name',
      placeholder: '真实姓名',
      ruleMessage: '请输入您的真实姓名',
      icon: '',
      required: custContact.value.Z2_real_name === 1,
      fieldProps: {
        maxlength: 50,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkBlankReg(val)) {
              return '请输入正确格式真实姓名';
            }

            if (val.length > 50) {
              return '真实姓名请输入50个以内字符';
            }
            return true;
          },
          trigger: formValues.value.contact_name ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: custContact.value.Z1_real_name === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'nickname',
      placeholder: '昵称',
      ruleMessage: '请输入您的昵称',
      icon: '',
      required: custContact.value.Z2_nickname === 1,
      fieldProps: {
        maxlength: 20,
        clearable: true,
        // formatter: (text: string) => trim(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkBlankReg(val)) {
              return '请输入正确昵称';
            }

            if (val.length > 32) {
              return '请输入32个以内字符';
            }
            return true;
          },
          trigger: formValues.value.nickname ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: custContact.value.Z1_nickname === 1,
    },
    {
      label: '',
      valueType: 'password',
      dataIndex: 'acctPwd',
      placeholder: '资金密码',
      ruleMessage: '请输入6位纯数字组成的资金密码',
      icon: 'password',
      fieldProps: {
        maxlength: 6,
        formatter: (text: string) => removeSpaces(text),
      },
      required: validateMaps.value.acctPwd.required,
      show: validateMaps.value.acctPwd.show,
    },
    {
      label: '',
      valueType: 'phone',
      dataIndex: 'phone',
      placeholder: '手机号',
      ruleMessage: '请输入手机号码',
      icon: 'cellphone',
      required: custContact.value.Z2_mobile_phone === 1,
      fieldProps: {
        maxlength: 11,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkPhoneReg(val)) {
              return '请输入正确的手机号';
            }
            return true;
          },
          trigger: formValues.value.phone ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: !isPhone.value && !isPhonePwdPhone.value && custContact.value.Z1_mobile_phone === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'zalo',
      placeholder: 'Zalo',
      ruleMessage: '请输入Zalo',
      icon: '',
      required: custContact.value.Z2_zalo === 1,
      fieldProps: {
        maxlength: 50,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: custContact.value.Z1_zalo === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'line',
      placeholder: 'Line',
      ruleMessage: '请输入Line',
      icon: '',
      required: custContact.value.Z2_line === 1,
      fieldProps: {
        maxlength: 50,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: custContact.value.Z1_line === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'facebook',
      placeholder: 'Facebook',
      ruleMessage: '请输入Facebook',
      icon: '',
      required: custContact.value.Z2_facebook === 1,
      fieldProps: {
        maxlength: 50,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: custContact.value.Z1_facebook === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'telegram',
      placeholder: 'Telegram',
      ruleMessage: '请输入Telegram',
      icon: '',
      required: custContact.value.Z2_telegram === 1,
      fieldProps: {
        maxlength: 50,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: custContact.value.Z1_telegram === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'whatsApp',
      placeholder: 'WhatsApp',
      ruleMessage: '请输入WhatsApp',
      icon: '',
      required: custContact.value.Z2_whatsApp === 1,
      fieldProps: {
        maxlength: 24,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: custContact.value.Z1_whatsApp === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'email',
      placeholder: '邮箱',
      ruleMessage: '请输入邮箱号',
      icon: '',
      required: custContact.value.Z2_email === 1,
      fieldProps: {
        maxlength: 50,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: !isEmailCode.value && custContact.value.Z1_email === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'qq',
      placeholder: 'QQ',
      ruleMessage: '请输入QQ号码',
      icon: '',
      required: custContact.value.Z2_qq === 1,
      fieldProps: {
        maxlength: 20,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: custContact.value.Z1_qq === 1,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'inviteCode',
      placeholder: '邀请码',
      ruleMessage: '请输入邀请码',
      icon: '',
      required: custContact.value.Z2_code === 1,
      fieldProps: {
        maxlength: 20,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show: custContact.value.Z1_code === 1,
    },
    {
      label: '',
      valueType: 'verifyCode',
      dataIndex: 'verifycode',
      placeholder: '验证码',
      ruleMessage: '请输入验证码',
      icon: 'yzm_code',
      required: true,
      fieldProps: {
        maxlength: 20,
        clearable: !isReadonlyInviteCode.value,
        readonly: isReadonlyInviteCode.value,
        formatter: (text: string) => removeSpaces(text),
      },
      show: isImageVerifyCode.value,
    },
  ].filter(item => item.show) as ColumnData[];
});

const validateFromData = (values: any) => {
  if (validateMaps.value.confirmPassword.show && validateMaps.value.confirmPassword.required) {
    if (values.password !== values.password2) {
      showNotify({ type: 'warning', message: mb_t('两次输入的密码不一致！') });
      return !1;
    }
  }
  return !0;
};

const onSubmit = async (values: any) => {
  console.log(values, 'onSubmit');
  console.log(formValues.value, window.location, 'onSubmit');
  // console.log(captchaIns, 'captchaIns');
  if (!validateFromData(values)) return;
  loading.value = true;
  await promiseTimeout(800);
  if (isAiVerify.value) {
    if (!isLoad.value) {
      showNotify({ type: 'warning', message: mb_t('智能验证加载失败，请刷新重试！') });
    } else {
      await showVerify(values);
    }
  } else {
    await handlerRegister(values);
  }
  loading.value = false;
};

const handlerRegister = async (params: Record<string, any>) => {
  try {
    const negProductId = await getNegProductID();
    await initNeg(negProductId);
    await getNegToken();
    loading.value = true;
    const options: any = {
      token: negToken.value, // 网易风控token
      isQp: 1,
      verifycode: undefined, // 图片验证
      validate: undefined, // 三方智能验证code
      ...omitBy(formValues.value, val => !val),
      ...omit(params, ['password2']),
      link_addr: window.location.origin,
    };

    if (formValues.value.inviteCode) {
      options.invite_code = formValues.value.inviteCode;
    }

    if (!isPhonePwd.value || (isPhonePwd.value && isPhonePwdPhone.value)) {
      options.mobile_phone = formValues.value?.phone ? ` ${getCountryCode()}-${formValues.value?.phone}` : '';
      options.phone = formValues.value?.phone;
    }
    console.log(options, 'handlerRegister options ----------------');
    console.log(formValues.value, 'handlerRegister formValues ----------------');
    let _registerType = props.registerType;
    if (isPhonePwd.value) {
      _registerType = isPhonePwdPhone.value ? 'phone_captcha' : 'account';
      if (!isPhonePwdPhone.value) {
        options.cust_name = formValues.value?.phone;
      }
      // delete options.phone;
    }
    console.log('handlerRegister start', _registerType, options);
    const res: any = await loginApi[_registerType]?.(options);
    console.log(res, 'handlerRegister结果');
    showNotify({ type: 'success', message: '注册成功' });
    saveAccountHandle();
    emit('success');
  } catch (error: any) {
    console.log(error, 'error');
    if (error?.info) {
      showFailToast(error.info);
    }
  } finally {
    loading.value = false;
  }
};

const getCountryCode = () => {
  return '+86';
  // return `+${replace(checkedCountry.value?.id, /[^0-9]/g, '')}`;
};

const getPromoteLinkCode = () => {
  let code = '';
  if (config.promote && config.promote.length !== 0) {
    code = config.promote;
  }
  if (tenantInfo.value.is_promote === 1) {
    code = md5(tenantInfo.value.domain_addr);
  }
  return code;
};

// 获取分享码
const getInviteCode = async () => {
  try {
    const res = await reqInvicode({
      link_addr: getPromoteLinkCode(),
    });
    if (res?.promote_code) {
      isReadonlyInviteCode.value = true;
      formValues.value.inviteCode = res.promote_code;
    } else {
      isReadonlyInviteCode.value = false;
    }
  } finally {
  }
};

const getFetchVerifyCode = async () => {
  await fetchVerifyCode({ type: 2 });
};

const selectCountry = (data: any) => {
  console.log(data, '000000000000000000000');
  checkedCountry.value = data;
};

const { initCaptcha, unloadCaptcha, showVerify, isLoad } = useAiValidate(2, handlerRegister);

const init = async () => {
  formRef.value?.resetValidation();
  if (isAiVerify.value) {
    initCaptcha();
  }
  if (custContact.value.Z1_code === 1) {
    await getInviteCode();
  }
  // await getFetchVerifyCode();
};

onMounted(async () => {
  await init();
});

onUnmounted(async () => {
  await unloadCaptcha();
});
</script>

<style lang="less" scoped>
.register-wrapper {
  padding: 0;
  max-height: 55vh;
  position: relative;
  overflow: hidden;

  .form-title {
    padding-left: 60px;
    color: #999;
  }

  .ai-validate {
    margin: 30px 50px 0;

    :deep(.yidun_intellisense) {
      .yidun_intelli-control {
        border-radius: 120px;
      }

      .yidun_modal {
        top: -400%;
      }
    }
  }

  .scroll-wrapper {
    max-height: 55vh;
    overflow-y: auto;
  }

  .van-form {
    .light {
      color: var(--theme-primary-color);
    }
  }

  :deep(.van-sticky) {
    transition: all 0.5s;
  }
}
</style>
